import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import SelectField from 'components/SelectField';
import { USAGE_REPORT_PERIOD } from 'models/EnterprisesAnalytics';
import { FormValues, USAGE_REPORT_PERIOD_LABEL_KEYS } from '../../data';
import UsageReportSectionsList from '../UsageReportSectionsList';
import { SIZE } from 'models/Size';
import { getUsageReportDateFormat, getUsageReportRange } from '../../utils';

import './ExportUsageReportForm.styles.scss';

type ExportUsageReportFormProps = {
  className?: string;
  earliestRecordDate?: Date;
  onSubmit: (values: FormValues) => void;
  onClose: () => void;
};

const ExportUsageReportForm: React.FC<ExportUsageReportFormProps> = (props) => {
  const { className, earliestRecordDate, onSubmit, onClose } = props;

  const { t } = useTranslation();

  const classes = classNames('export-usage-report-form', className);

  const periodOptions = useMemo(
    () =>
      Object.values(USAGE_REPORT_PERIOD).map((value) => ({
        value,
        label: t(USAGE_REPORT_PERIOD_LABEL_KEYS[value]),
      })),
    [t],
  );

  const initialValues: FormValues = useMemo(
    () => ({ period: USAGE_REPORT_PERIOD.LAST_MONTH }),
    [],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ submitting, values: { period }, handleSubmit }) => {
        const { range, comparisonRange } = getUsageReportRange(
          period,
          new Date(),
          earliestRecordDate,
        );

        return (
          <form
            className={classNames(classes, {
              'export-usage-report-form--disabled': submitting,
            })}
            onSubmit={handleSubmit}
          >
            <div className="export-usage-report-form__period">
              <Field
                name="period"
                label={t('period')}
                component={SelectField}
                options={periodOptions}
              />

              <p className="export-usage-report-form__note">
                {t('usageReportDateRange', {
                  start: getUsageReportDateFormat(range.start),
                  end: getUsageReportDateFormat(range.end),
                })}
                {comparisonRange &&
                  `, ${t('usageReportComparedWith', {
                    start: getUsageReportDateFormat(comparisonRange.start),
                    end: getUsageReportDateFormat(comparisonRange.end),
                  })}`}
              </p>
            </div>

            <div className="export-usage-report-form__includes">
              <p className="export-usage-report-form__includes-title">
                {t('usageReportIncludesTitle')}
              </p>

              <UsageReportSectionsList />
            </div>

            <div className="actions">
              <Button type="button" variant="link" onClick={onClose}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting}>
                {submitting && (
                  <Loader size={SIZE.SMALL} overlay={false} inline />
                )}
                {t('generateReport')}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default ExportUsageReportForm;
